// Motor genérico de reportes de Grupo Vértice CR
// - Toma columnas + filas y genera:
//   1. Preview HTML (tabla scrollable)
//   2. PDF (con branding, jsPDF + autoTable + DejaVu Sans para ₡)
//   3. Excel (xlsx-js-style con formato)
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "@/lib/boletaFont";

const FONT_FAMILY = "NomyProSans";

const COLOR_TITLE_BG = [60, 70, 65];
const COLOR_TITLE_FG = [255, 255, 255];
const COLOR_HEADER_BG = [225, 225, 220];
const COLOR_VALUE_BG = [248, 247, 244];
const COLOR_TEXT = [40, 40, 40];
const COLOR_BORDER = [180, 180, 180];
const COLOR_ACCENT = [80, 100, 90];
const COLOR_MUTED = [120, 120, 120];

function registerNomyFont(doc) {
  doc.addFileToVFS("NomyProSans-Regular.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("NomyProSans-Regular.ttf", FONT_FAMILY, "normal");
  doc.addFileToVFS("NomyProSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("NomyProSans-Bold.ttf", FONT_FAMILY, "bold");
}

export const fmtCRC = (n) =>
  "₡" + new Intl.NumberFormat("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
export const fmtFecha = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString("es-CR") : "—");
export const fmtNum = (n, dec = 2) => new Intl.NumberFormat("es-CR", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(Number(n) || 0);
export const fmtInt = (n) => new Intl.NumberFormat("es-CR").format(Math.round(Number(n) || 0));

// Formatea el valor de una celda según el tipo de columna
export function formatCell(row, col) {
  const raw = typeof col.value === "function" ? col.value(row) : row[col.key];
  if (raw == null || raw === "") return "—";
  switch (col.type) {
    case "money": return fmtCRC(raw);
    case "date": return fmtFecha(raw);
    case "number": return fmtNum(raw, col.dec ?? 2);
    case "int": return fmtInt(raw);
    case "bool": return raw ? "Sí" : "No";
    default: return String(raw);
  }
}

// Suma de una columna (solo si type=money|number|int)
export function sumColumn(rows, col) {
  if (!["money", "number", "int"].includes(col.type)) return null;
  return rows.reduce((acc, r) => {
    const raw = typeof col.value === "function" ? col.value(r) : r[col.key];
    return acc + (Number(raw) || 0);
  }, 0);
}

// === EXPORT A PDF ===
export function exportPDF({ titulo, subtitulo, empresa, filtros, columns, rows, orientation = "landscape", showTotals = true }) {
  const doc = new jsPDF({ unit: "mm", format: "letter", orientation });
  registerNomyFont(doc);
  doc.setFont(FONT_FAMILY, "normal");
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  let y = margin;

  // Encabezado con nombre de la empresa
  doc.setFillColor(...COLOR_TITLE_BG);
  doc.rect(margin, y, pageW - margin * 2, 11, "F");
  doc.setTextColor(...COLOR_TITLE_FG);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(13);
  doc.text(empresa?.razon_social || empresa?.nombre || "Empresa", pageW / 2, y + 7.5, { align: "center" });
  y += 13;

  // Título
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(15);
  doc.text(titulo || "Reporte", margin, y + 5);
  y += 8;
  if (subtitulo) {
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(subtitulo, margin, y + 3);
    y += 5;
  }

  // Chips de filtros
  if (filtros && filtros.length) {
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_MUTED);
    const chipsText = filtros.filter(Boolean).join("   ·   ");
    const lines = doc.splitTextToSize(chipsText, pageW - margin * 2);
    lines.forEach((line, i) => doc.text(line, margin, y + 4 + i * 3.2));
    y += 3 + lines.length * 3.2;
  }
  y += 2;

  // Tabla
  const head = [columns.map(c => c.label)];
  const body = rows.map(r => columns.map(c => formatCell(r, c)));
  // Fila de totales
  const foot = [];
  if (showTotals && rows.length > 0) {
    const totals = columns.map((c, idx) => {
      const s = sumColumn(rows, c);
      if (idx === 0) return `Total: ${rows.length} registro${rows.length === 1 ? "" : "s"}`;
      if (s == null) return "";
      return c.type === "money" ? fmtCRC(s) : c.type === "int" ? fmtInt(s) : fmtNum(s, c.dec ?? 2);
    });
    foot.push(totals);
  }

  autoTable(doc, {
    startY: y,
    head, body, foot,
    theme: "grid",
    styles: {
      font: FONT_FAMILY, fontSize: 7.5, cellPadding: 1.5,
      textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1,
      overflow: "linebreak",
    },
    headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold", halign: "center" },
    footStyles: { fillColor: COLOR_ACCENT, textColor: [255, 255, 255], fontStyle: "bold" },
    bodyStyles: { fillColor: COLOR_VALUE_BG },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: columns.reduce((acc, c, i) => {
      acc[i] = { halign: c.type === "money" || c.type === "number" || c.type === "int" ? "right" : "left" };
      if (c.width) acc[i].cellWidth = c.width;
      return acc;
    }, {}),
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      // Pie de página
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFont(FONT_FAMILY, "normal");
      doc.setFontSize(7);
      doc.setTextColor(...COLOR_MUTED);
      const fechaGen = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
      doc.text(`Generado el ${fechaGen} · ${empresa?.razon_social || empresa?.nombre || ""}`, pageW - margin, pageH - 5, { align: "right" });
      doc.text(`Página ${data.pageNumber}`, margin, pageH - 5);
    },
  });

  const safeName = (titulo || "reporte").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9_\-]+/g, "_");
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`${safeName}_${stamp}.pdf`);
}

// === EXPORT A EXCEL ===
export function exportExcel({ titulo, subtitulo, empresa, filtros, columns, rows, showTotals = true }) {
  const wb = XLSX.utils.book_new();
  const aoa = [];
  aoa.push([empresa?.razon_social || empresa?.nombre || "Empresa"]);
  aoa.push([titulo || "Reporte"]);
  if (subtitulo) aoa.push([subtitulo]);
  if (filtros && filtros.length) aoa.push([filtros.filter(Boolean).join(" · ")]);
  aoa.push([]); // fila vacía
  // Encabezado
  aoa.push(columns.map(c => c.label));
  const headerRow = aoa.length - 1;
  // Datos
  rows.forEach(r => {
    aoa.push(columns.map(c => {
      const raw = typeof c.value === "function" ? c.value(r) : r[c.key];
      if (raw == null || raw === "") return "";
      if (c.type === "money" || c.type === "number" || c.type === "int") return Number(raw) || 0;
      if (c.type === "date") return raw ? new Date(`${String(raw).slice(0, 10)}T00:00:00`) : "";
      if (c.type === "bool") return raw ? "Sí" : "No";
      return String(raw);
    }));
  });
  const dataStart = headerRow + 1;
  const dataEnd = aoa.length - 1;
  // Totales
  if (showTotals && rows.length > 0) {
    const totRow = columns.map((c, i) => {
      const s = sumColumn(rows, c);
      if (i === 0) return `Total: ${rows.length}`;
      if (s == null) return "";
      return s;
    });
    aoa.push(totRow);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Anchos
  ws["!cols"] = columns.map(c => ({ wch: c.width || (c.type === "money" ? 16 : c.type === "date" ? 12 : 22) }));
  // Estilo del encabezado (fila header)
  for (let i = 0; i < columns.length; i++) {
    const cellAddr = XLSX.utils.encode_cell({ r: headerRow, c: i });
    if (!ws[cellAddr]) continue;
    ws[cellAddr].s = {
      fill: { fgColor: { rgb: "E1E1DC" } },
      font: { bold: true, color: { rgb: "1A1A1A" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: { top: { style: "thin", color: { rgb: "B4B4B4" } }, bottom: { style: "thin", color: { rgb: "B4B4B4" } } },
    };
  }
  // Formato de columnas money/date/number
  for (let r = dataStart; r <= dataEnd; r++) {
    for (let ci = 0; ci < columns.length; ci++) {
      const c = columns[ci];
      const addr = XLSX.utils.encode_cell({ r, c: ci });
      if (!ws[addr]) continue;
      if (c.type === "money") ws[addr].z = "₡ #,##0.00";
      else if (c.type === "number") ws[addr].z = "#,##0.00";
      else if (c.type === "int") ws[addr].z = "#,##0";
      else if (c.type === "date") ws[addr].z = "dd/mm/yyyy";
    }
  }
  // Estilo fila totales
  if (showTotals && rows.length > 0) {
    const totR = aoa.length - 1;
    for (let i = 0; i < columns.length; i++) {
      const a = XLSX.utils.encode_cell({ r: totR, c: i });
      if (!ws[a]) continue;
      ws[a].s = {
        fill: { fgColor: { rgb: "50645A" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: columns[i].type === "money" || columns[i].type === "number" || columns[i].type === "int" ? "right" : "left" },
      };
      if (columns[i].type === "money") ws[a].z = "₡ #,##0.00";
      else if (columns[i].type === "number") ws[a].z = "#,##0.00";
      else if (columns[i].type === "int") ws[a].z = "#,##0";
    }
  }
  XLSX.utils.book_append_sheet(wb, ws, "Reporte");
  const safeName = (titulo || "reporte").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9_\-]+/g, "_");
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${safeName}_${stamp}.xlsx`);
}
